import { LogOut } from 'lucide-react';
import {
  Avatar,
  RailMenu,
  RailMenuContent,
  RailMenuItem,
  RailMenuSeparator,
  RailMenuTrigger,
} from '@wundo/ui';
import type { CurrentUser } from '@wundo/client/auth.ts';
import { personName } from '@/lib/personName.ts';
import { signOutAndLeave } from '@/lib/signOutAndLeave.ts';

/**
 * You, at the foot of the rail: your picture, or your initials in your own tone.
 *
 * A question mark while the user is still being fetched. The rail renders before
 * that answer arrives on purpose — blocking the whole frame on one disc is the
 * worse trade — and an empty circle reads as something broken.
 */
function AccountDisc({ user }: { user: CurrentUser | undefined }) {
  if (!user) return <Avatar className='size-full' name='?' seed='?' />;

  return (
    <Avatar
      className='size-full'
      src={user.profile_image_url}
      name={personName(user)}
      seed={user.email ?? user.id}
    />
  );
}

export interface UserMenuProps {
  user: CurrentUser | undefined;
  /* `bottom` from the phone's top bar; beside the rail otherwise. */
  side?: 'right' | 'bottom';
}

/* The account menu at the foot of the rail, or at the right of the phone's top bar: who you are, and the way out. */
export function UserMenu({ user, side = 'right' }: UserMenuProps) {
  return (
    <RailMenu>
      <RailMenuTrigger aria-label='Account'>
        <AccountDisc user={user} />
      </RailMenuTrigger>
      <RailMenuContent side={side}>
        <div className='px-2.75 py-2 text-sm'>
          <p className='truncate font-semibold text-ink'>
            {user ? personName(user) : '—'}
          </p>
          <p className='truncate text-xs text-ink-muted'>{user?.email}</p>
        </div>
        <RailMenuSeparator />
        <RailMenuItem tone='danger' onClick={() => void signOutAndLeave()}>
          <LogOut aria-hidden='true' />
          Sign out
        </RailMenuItem>
      </RailMenuContent>
    </RailMenu>
  );
}
